import { useEffect, useRef, useState } from "react";
import {
  getChatStatus,
  listChatSessions,
  getChatSession,
  deleteChatSession,
  sendChatMessage,
  type ChatMessage,
  type ChatSessionSummary,
} from "../api/client";

const DISABLED_MSG =
  "Chat is disabled. Configure the ANTHROPIC_API_KEY environment variable on the server to enable it.";

// ---- Helpers ----

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---- Message bubble ----

function Bubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: 12,
      }}
    >
      <div style={{ maxWidth: "80%", position: "relative" }}>
        <div
          style={{
            padding: "10px 14px",
            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
            background: isUser ? "#3b82f6" : "#f1f5f9",
            color: isUser ? "#fff" : "#1e293b",
            fontSize: 14,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            paddingBottom: !isUser && msg.cost_usd != null ? "22px" : "10px",
          }}
        >
          {msg.content}
          {!isUser && msg.cost_usd != null && msg.cost_usd > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: 6,
                right: 12,
                fontSize: 10,
                color: "#94a3b8",
                fontFamily: "monospace",
              }}
            >
              ~${msg.cost_usd < 0.001 ? msg.cost_usd.toFixed(6) : msg.cost_usd.toFixed(4)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Session list item ----

function SessionItem({
  session,
  active,
  onSelect,
  onDelete,
}: {
  session: ChatSessionSummary;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        padding: "10px 12px",
        cursor: "pointer",
        background: active ? "#e0f2fe" : "transparent",
        borderRadius: 8,
        marginBottom: 4,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 6,
        border: active ? "1px solid #bae6fd" : "1px solid transparent",
      }}
    >
      <div style={{ overflow: "hidden" }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#1e293b",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {session.first_message_preview || "(empty)"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
          {fmtDate(session.last_message_at)} · {session.message_count} msgs
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        title="Delete session"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#94a3b8",
          fontSize: 14,
          padding: 2,
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}

// ---- Main page ----

export default function ChatPage({ portfolioId }: { portfolioId: number | null }) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Check if chat is enabled
  useEffect(() => {
    getChatStatus()
      .then((res) => setEnabled(res.data.enabled))
      .catch(() => setEnabled(false));
  }, []);

  // Load sessions when enabled
  useEffect(() => {
    if (enabled) refreshSessions();
  }, [enabled]);

  // Load messages when active session changes
  useEffect(() => {
    if (!activeSessionId) {
      setMessages([]);
      return;
    }
    setLoadingHistory(true);
    getChatSession(activeSessionId)
      .then((res) => setMessages(res.data))
      .catch(() => setMessages([]))
      .finally(() => setLoadingHistory(false));
  }, [activeSessionId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const refreshSessions = () => {
    listChatSessions()
      .then((res) => setSessions(res.data))
      .catch(() => setSessions([]));
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);

    // Optimistically add the user message using a unique temp key
    const optimisticKey = crypto.randomUUID();
    const optimisticUser: ChatMessage = {
      id: 0,
      session_id: activeSessionId ?? "",
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    // Store optimisticKey in a data attribute via a wrapper trick — use a ref instead
    const markerId = `tmp:${optimisticKey}`;
    // We'll filter by content + timestamp combo for removal
    setMessages((prev) => [...prev, { ...optimisticUser, id: markerId as unknown as number }]);

    try {
      const res = await sendChatMessage({
        message: text,
        session_id: activeSessionId ?? undefined,
        portfolio_id: portfolioId ?? undefined,
      });
      const { session_id, reply, cost_usd } = res.data;

      // If this was a new session, switch to it
      if (activeSessionId !== session_id) {
        setActiveSessionId(session_id);
      }

      const assistantMsg: ChatMessage = {
        id: `tmp:${crypto.randomUUID()}` as unknown as number,
        session_id,
        role: "assistant",
        content: reply,
        cost_usd: cost_usd ?? null,
        created_at: new Date().toISOString(),
      };
      // Replace optimistic user msg with confirmed messages
      setMessages((prev) => [
        ...prev.filter((m) => String(m.id) !== markerId),
        { ...optimisticUser, session_id },
        assistantMsg,
      ]);
      refreshSessions();
    } catch {
      setMessages((prev) => prev.filter((m) => String(m.id) !== markerId));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await deleteChatSession(sessionId);
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setMessages([]);
      }
      refreshSessions();
    } catch {
      // ignore
    }
  };

  // ---- Render: disabled ----
  if (enabled === false) {
    return (
      <div style={{ padding: 32, color: "#64748b", fontSize: 14 }}>
        <h2 style={{ margin: "0 0 12px", color: "#1e293b" }}>🤖 AI Chat</h2>
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
            padding: "14px 18px",
            color: "#92400e",
            fontSize: 13,
          }}
        >
          {DISABLED_MSG}
        </div>
      </div>
    );
  }

  if (enabled === null) {
    return <div style={{ padding: 32, color: "#94a3b8" }}>Loading…</div>;
  }

  return (
    <div style={{ display: "flex", height: "calc(100vh - 60px)", gap: 0 }}>
      {/* ---- Session sidebar ---- */}
      <aside
        style={{
          width: 260,
          flexShrink: 0,
          background: "#fff",
          borderRight: "1px solid #e2e8f0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 12px 8px",
            fontWeight: 700,
            fontSize: 13,
            color: "#1e293b",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>🤖 Chat History</span>
          <button
            onClick={handleNewSession}
            title="New conversation"
            style={{
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            + New
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
          {sessions.length === 0 && (
            <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", marginTop: 24 }}>
              No conversations yet
            </div>
          )}
          {sessions.map((s) => (
            <SessionItem
              key={s.session_id}
              session={s}
              active={s.session_id === activeSessionId}
              onSelect={() => setActiveSessionId(s.session_id)}
              onDelete={() => handleDeleteSession(s.session_id)}
            />
          ))}
        </div>
      </aside>

      {/* ---- Chat area ---- */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#f8fafc" }}>
        {/* Header */}
        <div
          style={{
            padding: "12px 16px",
            background: "#fff",
            borderBottom: "1px solid #e2e8f0",
            fontSize: 13,
            color: "#64748b",
          }}
        >
          {activeSessionId
            ? `Session: ${activeSessionId.slice(0, 8)}…`
            : "New conversation"}
          <span style={{ marginLeft: 8, color: "#94a3b8", fontSize: 11 }}>
            (Press Enter to send, Shift+Enter for new line)
          </span>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          {loadingHistory && (
            <div style={{ color: "#94a3b8", fontSize: 13, textAlign: "center" }}>Loading…</div>
          )}
          {!loadingHistory && messages.length === 0 && (
            <div style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", marginTop: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🤖</div>
              <div>Ask me anything about your portfolio!</div>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                I can query positions, trades, analytics, benchmarks, and market data.
              </div>
            </div>
          )}
          {messages.map((msg, idx) => (
            <Bubble key={msg.id || idx} msg={msg} />
          ))}
          {sending && (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
              <div
                style={{
                  padding: "10px 16px",
                  borderRadius: "18px 18px 18px 4px",
                  background: "#f1f5f9",
                  color: "#94a3b8",
                  fontSize: 14,
                }}
              >
                ⋯
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div
          style={{
            padding: "12px 16px",
            background: "#fff",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
          }}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your portfolio…"
            rows={2}
            disabled={sending}
            style={{
              flex: 1,
              padding: "10px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 10,
              fontSize: 14,
              resize: "none",
              fontFamily: "inherit",
              outline: "none",
              color: "#1e293b",
              background: sending ? "#f8fafc" : "#fff",
            }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !input.trim()}
            style={{
              padding: "10px 18px",
              background: sending || !input.trim() ? "#94a3b8" : "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: sending || !input.trim() ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {sending ? "…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
